import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, FaTimes, FaDollarSign, FaCalendarAlt, 
  FaUsers, FaChartLine, FaFilter, FaPlus, FaHeart, FaShare,
  FaGraduationCap, FaLaptop, FaBook, FaTree, FaHospital
} from 'react-icons/fa';
import { useCampaigns } from '../hooks/CampaignContext';
import { useAuth } from '../hooks/useAuth';

// Sample campaign data
const sampleCampaigns = [
  {
    id: '1',
    title: 'Vidyarthi Sahayata Kosh: Empowering Rural Students',
    description: 'Provide educational opportunities for talented students from rural and economically disadvantaged backgrounds across India.',
    currentAmount: 1500000,
    goalAmount: 2500000,
    endDate: '2024-12-31',
    category: 'Education',
    organizer: 'Alumni Association',
    supporters: 520,
    image: 'https://images.unsplash.com/photo-1627556704302-624286467c65?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-05-15', content: 'We\'ve reached 60% of our goal! 50 students already benefiting.' },
      { date: '2024-03-01', content: 'First batch of scholarships awarded to 25 deserving students from 10 states.' }
    ],
    tags: ['Education', 'Rural Development', 'Empowerment']
  },
  {
    id: '2',
    title: 'Atmanirbhar Innovation Lab Expansion',
    description: 'Support the expansion of our university\'s innovation lab to foster entrepreneurship and cutting-edge research in line with Make in India initiative.',
    currentAmount: 10000000,
    goalAmount: 25000000,
    endDate: '2025-06-30',
    category: 'Research',
    organizer: 'University Research Department',
    supporters: 180,
    image: 'https://images.unsplash.com/photo-1581092921461-eab62e97a780?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-06-01', content: 'Construction begins on the new wing of the Atmanirbhar Innovation Lab.' },
      { date: '2024-04-15', content: 'Major donation received from leading Indian IT company.' }
    ],
    tags: ['Research', 'Innovation', 'Make in India']
  },
  {
    id: '3',
    title: 'Swachh aur Harit Campus Abhiyan',
    description: 'Transform our campus with solar energy, water harvesting, and innovative waste management solutions.',
    currentAmount: 3000000,
    goalAmount: 7500000,
    endDate: '2024-09-30',
    category: 'Environment',
    organizer: 'Student Environmental Council',
    supporters: 750,
    image: 'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-05-22', content: 'Solar panels installed, covering 30% of campus energy needs.' },
      { date: '2024-04-10', content: 'Rainwater harvesting system operational in 5 campus buildings.' }
    ],
    tags: ['Environment', 'Sustainability', 'Swachh Bharat']
  },
  {
    id: '4',
    title: 'Mann Mitra: Alumni Mental Health Initiative',
    description: 'Establish a comprehensive mental health support system for students and alumni, addressing the rising concerns in Indian youth.',
    currentAmount: 2250000,
    goalAmount: 4000000,
    endDate: '2024-10-31',
    category: 'Health',
    organizer: 'Alumni Wellness Committee',
    supporters: 310,
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-06-10', content: '24/7 mental health helpline launched in Hindi and English.' },
      { date: '2024-05-01', content: 'Partnership with NIMHANS for specialized counseling services.' }
    ],
    tags: ['Mental Health', 'Wellness', 'Youth Support']
  },
  {
    id: '5',
    title: 'Vishwa Gyan: Global Exchange Program',
    description: 'Fund international exchange opportunities for Indian students to gain global exposure and bring world-class skills back to India.',
    currentAmount: 6000000,
    goalAmount: 10000000,
    endDate: '2025-03-31',
    category: 'Education',
    organizer: 'International Affairs Office',
    supporters: 280,
    image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-06-15', content: 'New partnerships established with universities in USA, Germany, and Japan.' },
      { date: '2024-04-30', content: '50 students selected for upcoming semester exchange programs.' }
    ],
    tags: ['Global Education', 'Skill Development', 'Cultural Exchange']
  },
  {
    id: '6',
    title: 'Gram Swasthya Abhiyan',
    description: 'Organize health camps and awareness programs to promote preventive healthcare in rural communities near our campus.',
    currentAmount: 1500000,
    goalAmount: 3000000,
    endDate: '2024-11-30',
    category: 'Health',
    organizer: 'Alumni Health Initiative',
    supporters: 450,
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-06-20', content: 'First health camp conducted in 5 villages, reaching over 2000 people.' },
      { date: '2024-05-05', content: 'Partnership established with Apollo Hospitals for medical supplies.' }
    ],
    tags: ['Rural Health', 'Community Service', 'Healthcare']
  },
  {
    id: '7',
    title: 'Sanskriti Sangam: Cultural Festival',
    description: 'Support our annual arts and culture festival showcasing India\'s diverse cultural heritage and student talents.',
    currentAmount: 750000,
    goalAmount: 1500000,
    endDate: '2024-12-15',
    category: 'Culture',
    organizer: 'Cultural Affairs Committee',
    supporters: 130,
    image: 'https://images.unsplash.com/photo-1601877225473-a0d1b1090a86?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-07-10', content: 'Renowned classical dancers confirmed for opening ceremony.' },
      { date: '2024-06-01', content: 'Festival dates announced, featuring performances from 20 states.' }
    ],
    tags: ['Indian Culture', 'Arts', 'Diversity']
  },
  {
    id: '8',
    title: 'Nari Shakti in STEM Scholarship Fund',
    description: 'Empower women in STEM fields by supporting scholarships for female students across India.',
    currentAmount: 4500000,
    goalAmount: 7500000,
    endDate: '2025-02-28',
    category: 'Education',
    organizer: 'Women Empowerment Committee',
    supporters: 360,
    image: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-07-20', content: 'Scholarships awarded to 50 female students in engineering and technology.' },
      { date: '2024-06-10', content: 'Partnership formed with Infosys and TCS to support the fund.' }
    ],
    tags: ['Women in STEM', 'Gender Equality', 'Education']
  },
  {
    id: '9',
    title: 'Digital Library Transformation Project',
    description: 'Create a state-of-the-art digital library to provide students with access to global resources and support digital learning initiatives.',
    currentAmount: 5500000,
    goalAmount: 10000000,
    endDate: '2024-08-31',
    category: 'Infrastructure',
    organizer: 'Campus Facilities Management',
    supporters: 220,
    image: 'https://images.unsplash.com/photo-1568667256549-094345857637?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-05-18', content: 'Partnership with National Digital Library of India established.' },
      { date: '2024-04-02', content: 'High-speed internet and computer lab installation completed.' }
    ],
    tags: ['Digital Education', 'Library', 'E-Learning']
  },
  {
    id: '10',
    title: 'Digital Saksharta Abhiyan for Rural Schools',
    description: 'Provide computers, internet access, and digital literacy training to students in rural schools across 5 states.',
    currentAmount: 3000000,
    goalAmount: 6000000,
    endDate: '2025-01-15',
    category: 'Education',
    organizer: 'Alumni Outreach Program',
    supporters: 400,
    image: 'https://images.unsplash.com/photo-1516321165247-4aa89a48be28?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    updates: [
      { date: '2024-07-25', content: 'Computer labs set up in 20 rural schools across Maharashtra and Gujarat.' },
      { date: '2024-05-30', content: 'Partnership with NASSCOM Foundation for curriculum development.' }
    ],
    tags: ['Digital Literacy', 'Rural Education', 'Skill India']
  }
];
const DonationForm = ({ campaign, onClose, onDonate }) => {
  const [amount, setAmount] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onDonate(campaign.id, parseFloat(amount));
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="bg-white rounded-lg p-6 w-full max-w-md"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Donate to {campaign.title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="amount">
              Donation Amount (₹)
            </label>
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              min="1"
              step="1"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter donation amount"
            />
          </div>
          <button
            type="submit"
            className="w-full bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition duration-300"
          >
            Confirm Donation
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};
const CampaignCard = ({ campaign, onDonate, userDonations }) => {
  const progress = (campaign.currentAmount / campaign.goalAmount) * 100;
  const hasDonated = userDonations.includes(campaign.id);

  return (
    <motion.div 
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white p-6 rounded-lg shadow-md mb-6 hover:shadow-lg transition-shadow duration-300"
    >
      <div className="flex flex-col md:flex-row">
        <div className="md:w-1/3 mb-4 md:mb-0 md:mr-6">
          <img src={campaign.image} alt={campaign.title} className="w-full h-72 object-cover rounded-lg" />
        </div>
        <div className="md:w-2/3">
          <h3 className="text-2xl font-bold text-gray-800 mb-2">{campaign.title}</h3>
          <p className="text-gray-600 mb-4">{campaign.description}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {campaign.tags.map((tag, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {tag}
              </span>
            ))}
          </div>
          <div className="flex items-center mb-4">
            <FaUsers className="text-gray-500 mr-2" />
            <span className="text-gray-700">{campaign.supporters} supporters</span>
          </div>
          <div className="mb-4">
            <div className="flex justify-between mb-1">
              <span className="text-base font-medium text-blue-700">${campaign.currentAmount} raised</span>
              <span className="text-sm font-medium text-blue-700">{progress.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
          <div className="flex justify-between items-center">
        <span className="text-gray-500">
          <FaCalendarAlt className="inline mr-1" /> Ends {campaign.endDate}
        </span>
        {hasDonated ? (
          <span className="text-green-500 font-semibold flex items-center">
            <FaHeart className="mr-2" /> Donated
          </span>
        ) : (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onDonate(campaign)}
            className="bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 transition duration-300"
          >
            <FaDollarSign className="inline mr-2" /> Donate Now
          </motion.button>
        )}
      </div>
        </div>
      </div>
    </motion.div>
  );
};

const CreateCampaignForm = ({ onClose, onSubmit }) => {
  const [campaignData, setCampaignData] = useState({
    title: '',
    description: '',
    goalAmount: '',
    endDate: '',
    category: '',
    image: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCampaignData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(campaignData);
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
    >
      <div className="p-6 flex-grow overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Create New Campaign</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaPlus className="transform rotate-45" size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Campaign Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={campaignData.title}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter campaign title"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={campaignData.description}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Enter campaign description"
            ></textarea>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="goalAmount">
              Goal Amount ($)
            </label>
            <input
              type="number"
              id="goalAmount"
              name="goalAmount"
              value={campaignData.goalAmount}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter goal amount"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="endDate">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={campaignData.endDate}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
              Category
            </label>
            <select
              id="category"
              name="category"
              value={campaignData.category}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Category</option>
              <option value="Education">Education</option>
              <option value="Research">Research</option>
              <option value="Environment">Environment</option>
              <option value="Health">Health</option>
              <option value="Community">Community</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="image">
              Campaign Image URL
            </label>
            <input
              type="url"
              id="image"
              name="image"
              value={campaignData.image}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter image URL"
            />
          </div>
        </form>
      </div>
      <div className="p-6 bg-gray-100 flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
        >
          Create Campaign
        </button>
      </div>
    </motion.div>
  );
};

const CampaignsPage = () => {
  const { campaigns, addCampaign } = useCampaigns();
  const { user, canCreateCampaign } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCampaigns, setFilteredCampaigns] = useState(sampleCampaigns);
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    category: [],
    sortBy: 'endDate'
  });

  const [donationForm, setDonationForm] = useState({ isOpen: false, campaign: null });
  const [userDonations, setUserDonations] = useState([]);
  useEffect(() => {
    setFilteredCampaigns(sampleCampaigns);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const filtered = sampleCampaigns.filter(campaign => 
      campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCampaigns(filtered);
  };

  const handleDonate = (campaign) => {
    setDonationForm({ isOpen: true, campaign: campaign });
  };
  const handleDonationSubmit = (campaignId, amount) => {
    console.log(`Donating ₹${amount} to campaign: ${campaignId}`);
    setUserDonations([...userDonations, campaignId]);
    setFilteredCampaigns(prevCampaigns => 
      prevCampaigns.map(camp => 
        camp.id === campaignId 
          ? { ...camp, currentAmount: camp.currentAmount + amount, supporters: camp.supporters + 1 }
          : camp
      )
    );
    setDonationForm({ isOpen: false, campaign: null }); // Close the form after donation
  };
  const handleCreateCampaign = (newCampaign) => {
    const campaignWithId = {
      ...newCampaign,
      id: Date.now().toString(),
      currentAmount: 0,
      supporters: 0,
      organizer: user.fullName,
      updates: [],
      tags: [newCampaign.category]
    };
    addCampaign(campaignWithId);
    setFilteredCampaigns(prev => [campaignWithId, ...prev]);
    setCreateCampaignOpen(false);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [category]: prevFilters[category].includes(value)? prevFilters[category].filter(item => item !== value)
      : [...prevFilters[category], value]
  }));
};

const applySortAndFilters = () => {
  let filtered = [...sampleCampaigns];
  
  if (filters.category.length > 0) {
    filtered = filtered.filter(campaign => filters.category.includes(campaign.category));
  }

  switch (filters.sortBy) {
    case 'endDate':
      filtered.sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
      break;
    case 'goalAmount':
      filtered.sort((a, b) => b.goalAmount - a.goalAmount);
      break;
    case 'progress':
      filtered.sort((a, b) => (b.currentAmount / b.goalAmount) - (a.currentAmount / a.goalAmount));
      break;
    default:
      break;
  }

  setFilteredCampaigns(filtered);
  setFilterOpen(false);
};

const categoryIcons = {
  Education: FaGraduationCap,
  Research: FaLaptop,
  Environment: FaTree,
  Health: FaHospital,
  Community: FaUsers
};

return (
  <>
    <header className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">Fundraising Campaigns</h1>
          <p className="text-xl max-w-2xl">The Donation Portal enables both alumni and the institution to contribute to and manage financial donations through various campaigns.
          </p>
        </div>
      </header>
  <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto">

      <div className="mb-8 flex flex-wrap gap-4">
        <form onSubmit={handleSearch} className="flex-grow">
          <div className="relative">
            <input
              type="text"
              placeholder="Search campaigns..."
              className="w-full p-4 pl-12 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </form>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setFilterOpen(!filterOpen)}
          className="bg-blue-500 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-blue-600 transition duration-300"
        >
          <FaFilter className="mr-2" /> Filters
        </motion.button>
        {canCreateCampaign && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setCreateCampaignOpen(true)}
            className="bg-green-500 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-green-600 transition duration-300"
          >
            <FaPlus className="mr-2" /> Create Campaign
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {filterOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white p-6 rounded-lg shadow-md mb-8"
          >
            <h2 className="text-xl font-semibold mb-4">Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold mb-2">Category</h3>
                {Object.keys(categoryIcons).map(category => {
                  const Icon = categoryIcons[category];
                  return (
                    <label key={category} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={filters.category.includes(category)}
                        onChange={() => handleFilterChange('category', category)}
                        className="mr-2"
                      />
                      <Icon className="mr-2 text-blue-500" />
                      {category}
                    </label>
                  );
                })}
              </div>
              <div>
                <h3 className="font-semibold mb-2">Sort By</h3>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="endDate">End Date</option>
                  <option value="goalAmount">Goal Amount</option>
                  <option value="progress">Progress</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={applySortAndFilters}
                className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition duration-300"
              >
                Apply Filters
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div>
      <AnimatePresence>
  {filteredCampaigns.map(campaign => (
    <CampaignCard 
      key={campaign.id} 
      campaign={campaign} 
      onDonate={handleDonate}
      userDonations={userDonations}
    />
  ))}
</AnimatePresence>
      </div>

      <AnimatePresence>
        {createCampaignOpen && (
          <CreateCampaignForm
            onClose={() => setCreateCampaignOpen(false)}
            onSubmit={handleCreateCampaign}
          />
        )}
        {donationForm.isOpen && (
          <DonationForm
            campaign={donationForm.campaign}
            onClose={() => setDonationForm({ isOpen: false, campaign: null })}
            onDonate={handleDonationSubmit}
          />
        )}
      </AnimatePresence>
    </div>
  </div>
  </>
);
};

export default CampaignsPage;